import { LoadingButton } from "@mui/lab";
import { Form, FormSpy } from "react-final-form";
import { Box, Grid, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Captcha from "./Captcha";
import CaptureRequesterDetails from "./CaptureRequesterDetails";
import {
  attendingAppointment,
  captchaSuccessful,
  detailsCaptured,
  isAgent,
  requesterDetailsFormState,
  setAttendingAppointment,
  setCaptchaSubmissionStarted,
  setDetailsCaptured,
  submitCaptchaAsync,
  updateRequesterDetailsFormState,
} from "../../features/CaptureDetailsSlice";
import { setDetailsSubmissionStarted, submitCaptureDetailsFormAsync, displayMaskedTaxrefModal } from "../../features/BookingsSlice";
import { FormState, FormApi } from "final-form";
import { getImageLocation, invalidRepresentative, invalidRepresentativeDelegate, invalidTaxPractitioner, invalidTaxPractitionerDelegate } from "../../features/Helper";
import { invalidAlternativeNumber, southAfricanPassportCountry } from "./FormValidationHelper";
import { useEffect } from "react";
import MaskedTaxRefSelectModal from "./MaskedTaxRefSelectModal";

const CaptureDetails = () => {
  const dispatch = useDispatch();
  const requesterDetailsForm = useSelector(requesterDetailsFormState);
  const formDetailsCaptured = useSelector(detailsCaptured);
  const agent = useSelector(isAgent);
  const successfulCaptcha = useSelector(captchaSuccessful);
  const attending = useSelector(attendingAppointment);
  const displayModal = useSelector(displayMaskedTaxrefModal);

  const matches = useMediaQuery("(max-width:900px)");

  useEffect(() => {
    if(successfulCaptcha) {
      dispatch(setDetailsSubmissionStarted(true));
      dispatch(submitCaptureDetailsFormAsync());
    }
  }, [dispatch, successfulCaptcha, agent]);

  const handleNext = () => {
    if (agent && requesterDetailsForm?.valid) {
      dispatch(setDetailsCaptured(true));
      dispatch(setDetailsSubmissionStarted(true));
      dispatch(submitCaptureDetailsFormAsync());
    } else if (requesterDetailsForm?.valid && !formDetailsCaptured) {
      dispatch(setDetailsCaptured(true));
    } else if (formDetailsCaptured && !successfulCaptcha) {
      dispatch(setCaptchaSubmissionStarted(true));
      dispatch(submitCaptchaAsync());      
    } else if (successfulCaptcha) {
      dispatch(setDetailsSubmissionStarted(true));
      dispatch(submitCaptureDetailsFormAsync());
    }
  };

  const entityTypeChanged = (form: FormApi<Record<string, any>, Partial<Record<string, any>>>) => {
    form.change("smmeType", null);
    form.change("companyRegNo", null);
    form.change('trustType', null);
    form.change('trustRegNo', null);
    form.change('taxpayer_idType', null);
    form.change('taxpayer_idNumber', null);
    form.change('taxpayer_asylumNo', null);
    form.change('taxpayer_TRN', null);
    form.change('taxpayer_passportNo', null);
    form.change('taxpayer_passportDate', null);
    form.change('taxpayer_passportCountry', null);
  }

  const resetDelegateDetails = (form: FormApi<Record<string, any>, Partial<Record<string, any>>>) => {
    form.change("delegate_idType", null);
    form.change("delegate_idNumber", null);
    form.change('delegate_asylumNo', null);
    form.change('delegate_TRN', null);
    form.change('delegate_passportNo', null);
    form.change('delegate_passportDate', null);
    form.change('delegate_passportCountry', null);
  }

  return (
    <Box
      className="capture-details-form-container"
      sx={(theme) => ({
        display: "flex",
        flexDirection: "row",
        [theme.breakpoints.down("md")]: {
          flexDirection: "column",
        },
        justifyContent: "space-between",
        height: 1,
        width: "100%",
      })}
      role="contentinfo"
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flex: "0 0 40%",
          [theme.breakpoints.down("md")]: {
            flex: "0 0 20%",
          },
        })}
      >
        <img
          id="capture-details-image"
          src={
            matches
              ? getImageLocation("capture-details-img-mobile.png")
              : getImageLocation("capture-details-img.png")
          }
          alt="woman smiling at phone"
          style={{ width: "100%", objectFit: "cover" }}
          aria-label="woman smiling at phone"
        />
      </Box>

      <Box sx={{ display: "block", flex: "auto" }}>
        <Grid
          container
          sx={{
            p: "22px",
            height: 1,
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            overflowY: 'auto'
          }}
        >
          <section
            style={{
              display: "flex",
              justifyContent: "flex-start",
              borderBottom: "1px solid #E1E1E1",
              paddingBottom: "22px",
              width: "100%",
              height: "fit-content",
            }}
          >
            <h1 style={{color: "#00558B", fontWeight: "normal", marginTop: "4px", marginBottom: "4px", fontSize: "32px"}}>
              Capture Details
            </h1>
          </section>

          <Grid
            container
            spacing={1}
            sx={{
              height: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              flexWrap: "nowrap",
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Form onSubmit={() => {}}>
                {({
                  handleSubmit,
                  submitting,
                  pristine,
                  values,
                  form,
                  errors,
                }) => (
                  <>
                    <Grid container spacing={1} direction="column">
                      <FormSpy
                        onChange={(
                          state: FormState<
                            Record<string, any>,
                            Partial<Record<string, any>>
                          >
                        ) => {
                          form.change('taxType', 'income_tax');
                          dispatch(updateRequesterDetailsFormState(state));

                          if (
                            requesterDetailsForm?.values.requestorType !==
                            state?.values.requestorType
                          ) {
                            dispatch(setAttendingAppointment(true));
                            dispatch(updateRequesterDetailsFormState(state));
                            form.change("entityType", null);
                            entityTypeChanged(form);
                          }

                          if (
                            requesterDetailsForm?.values.entityType !==
                            state?.values.entityType
                          ) {
                            entityTypeChanged(form);
                          }

                          if(attending) {
                            resetDelegateDetails(form);
                          }
                        }}
                      />
                      { displayModal && <MaskedTaxRefSelectModal /> }
                      
                      <Grid item>
                        {!formDetailsCaptured || agent
                          ? (<CaptureRequesterDetails />) 
                          : null
                        }

                        {formDetailsCaptured && !agent
                          ? (<Captcha />)
                          : null
                        }
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        mt: "32px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <LoadingButton
                        id="clear-form"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          if (!formDetailsCaptured) {
                            form.reset();
                            dispatch(setAttendingAppointment(true));
                          } else {
                            dispatch(setDetailsCaptured(false));
                          }
                        }}
                      >
                        {!formDetailsCaptured ? "Clear" : "Back"}
                      </LoadingButton>

                      <LoadingButton
                        id="continue-to-upload"
                        variant="contained"
                        color="primary"
                        disabled={
                          (!requesterDetailsForm?.valid || invalidAlternativeNumber(requesterDetailsForm) || invalidTaxPractitioner(requesterDetailsForm) || invalidRepresentative(requesterDetailsForm) || invalidTaxPractitionerDelegate(requesterDetailsForm) || invalidRepresentativeDelegate(requesterDetailsForm) || southAfricanPassportCountry(requesterDetailsForm)) ??
                          false
                        }
                        onClick={handleNext}
                      >
                        {!formDetailsCaptured && !agent ? "Next" : "Submit"}
                      </LoadingButton>
                    </Box>
                  </>
                )}
              </Form>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CaptureDetails;
